import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Sparkles, ArrowLeft, Printer, RefreshCw } from "lucide-react";
import Logo from "@/components/Logo";
import AuthModal from "@/components/AuthModal";
import { useI18n } from "@/i18n/LanguageContext";
import { DEFAULT_SETTINGS, fetchSettings } from "@/siteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-pine/15 bg-white px-4 py-3 text-sm font-light text-bark placeholder:text-sand/60 focus:border-copper focus:outline-none transition-colors duration-300";

export default function AdminPage() {
  const { t } = useI18n();
  const a = t.admin;
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState(null);
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("");
  const [articleLang, setArticleLang] = useState("fr");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [requestsError, setRequestsError] = useState(false);
  const [diag, setDiag] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const loadRequests = () => {
    setRequestsError(false);
    setRequests(null);
    axios
      .get(`${API}/admin/requests`, { withCredentials: true })
      .then((r) => setRequests(r.data))
      .catch(() => setRequestsError(true));
    axios.get(`${API}/admin/diagnostics`, { withCredentials: true }).then((r) => setDiag(r.data)).catch(() => setDiag(null));
  };

  const resetBox = async () => {
    if (!window.confirm(a.resetConfirm)) return;
    try {
      await axios.delete(`${API}/admin/requests`, { withCredentials: true });
      toast.success(a.resetDone);
      loadRequests();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t.auth.errorGeneric);
    }
  };

  const removeRequest = async (id) => {
    if (!window.confirm(a.deleteConfirm)) return;
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { withCredentials: true });
      setRequests((list) => (list || []).filter((x) => x.id !== id));
      toast.success(a.requestDeleted);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t.auth.errorGeneric);
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(r.data);
      } catch (e) {
        if (e.response?.status === 401) {
          try {
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(r.data);
            return;
          } catch {}
        }
        setUser(null);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadRequests();
    axios.get(`${API}/articles`).then((r) => setArticles(r.data)).catch(() => {});
    fetchSettings().then(setSettings);
  }, [user]);

  const generate = async () => {
    if (!topic.trim() || generating) return;
    setGenerating(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/articles`,
        { topic: topic.trim(), lang: articleLang },
        { withCredentials: true }
      );
      setArticles((list) => [data, ...list]);
      setTopic("");
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : a.restricted);
    } finally {
      setGenerating(false);
    }
  };

  const removeArticle = async (id) => {
    try {
      await axios.delete(`${API}/admin/articles/${id}`, { withCredentials: true });
      setArticles((list) => list.filter((x) => x.id !== id));
    } catch {}
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, settings, { withCredentials: true });
      setSettings(data);
      toast.success(a.settingsSaved);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t.auth.errorGeneric);
    } finally {
      setSavingSettings(false);
    }
  };

  const printLabel = (r) => {
    const digits = (String(r.id).replace(/\D/g, "") + String(new Date(r.created_at).getTime()))
      .padEnd(12, "0")
      .slice(0, 12);
    const ref = digits.replace(/(\d{4})(?=\d)/g, "$1 ");
    const recipientAddr = [r.address, [r.postal_code, r.city].filter(Boolean).join(" "), r.country]
      .filter(Boolean)
      .join("<br/>") || "—";
    const contentLine = (r.varieties || []).length
      ? `Café de spécialité — ${r.varieties.join(", ")}`
      : "Café de spécialité";
    const w = window.open("", "_blank", "width=480,height=740");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Monte Yolli — ${ref}</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
<style>
  @page { size: 100mm 150mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; }
  .label { width: 100mm; height: 150mm; border: 1.2mm solid #000; padding: 3mm 3.5mm; display: flex; flex-direction: column; overflow: hidden; }
  .head { display: flex; align-items: center; gap: 4mm; border-bottom: 0.6mm solid #000; padding-bottom: 2.5mm; }
  .head img { width: 24mm; height: 24mm; border-radius: 50%; object-fit: cover; border: 0.5mm solid #000; }
  .head .sender { flex: 1; }
  .tag { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.5mm; }
  .name { font-size: 12pt; font-weight: 800; }
  .addr { font-size: 7.5pt; line-height: 1.35; }
  .sec { border-bottom: 0.6mm solid #000; padding: 2.5mm 0; }
  .recipient-row { display: flex; justify-content: space-between; gap: 3mm; }
  .rname { font-size: 12.5pt; font-weight: 800; }
  .raddr { font-size: 8.5pt; line-height: 1.4; }
  .express { border: 0.8mm solid #000; padding: 1.5mm 3mm; text-align: center; font-weight: 800; height: fit-content; }
  .express .n { font-size: 13pt; }
  .express .w { font-size: 6.5pt; letter-spacing: 0.5mm; }
  .track { font-size: 8pt; font-weight: 700; letter-spacing: 0.4mm; }
  .bignum { font-size: 15pt; font-weight: 800; letter-spacing: 0.5mm; margin: 1mm 0; }
  .code-row { display: flex; align-items: center; gap: 3mm; }
  #bc { max-width: 62mm; height: 12mm; }
  .qr { width: 17mm; height: 17mm; }
  .details { display: flex; justify-content: space-between; gap: 3mm; font-size: 7.5pt; line-height: 1.5; }
  .details b { letter-spacing: 0.4mm; }
  .foot { display: flex; justify-content: space-between; align-items: center; padding-top: 2mm; font-size: 8pt; font-weight: 700; }
  .up { border: 0.7mm solid #000; padding: 1mm 2mm; text-align: center; font-size: 10pt; font-weight: 800; line-height: 1; }
  .up span { display: block; font-size: 4.5pt; letter-spacing: 0.3mm; margin-top: 1mm; }
  @media print { body { margin: 0; } }
</style></head><body>
<div class="label">
  <div class="head">
    <img src="${window.location.origin}/assets/logo-official.jpg" alt="Monte Yolli"/>
    <div class="sender">
      <div class="tag">EXPÉDITEUR / SENDER</div>
      <div class="name">Monte Yolli</div>
      <div class="addr">Durango Pte. 303, Colonia Centro<br/>C.P. 43600, Tulancingo de Bravo, Hidalgo, México<br/>Tél. : +52 775 105 2230<br/>Email : distribucion@monteyolli.com</div>
    </div>
  </div>
  <div class="sec recipient-row">
    <div>
      <div class="tag">DESTINATAIRE / RECIPIENT</div>
      <div class="rname">${r.name}</div>
      <div class="raddr"><strong>${r.business}</strong><br/>${recipientAddr}<br/>Tél. : ${r.phone || "—"}<br/>${r.email}</div>
    </div>
    <div class="express"><div class="w">EXPRESS</div><div class="n">1/1</div></div>
  </div>
  <div class="sec">
    <div class="track">RÉFÉRENCE / TRACKING</div>
    <div class="bignum">${ref}</div>
    <div class="code-row">
      <svg id="bc"></svg>
      <img class="qr" src="https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=https://monteyolli.com/suivi/${digits}" alt="QR"/>
    </div>
  </div>
  <div class="sec details">
    <div>
      <b>CONTENU / CONTENT</b> ${contentLine}<br/>
      <b>VOLUME</b> ${r.volume}${r.sample_box ? "<br/><b>COFFRET B2B</b> ✓" : ""}<br/>
      <b>DATE</b> ${new Date(r.created_at).toLocaleDateString("fr-CA")}
    </div>
  </div>
  <div class="foot">
    <div>Café de spécialité<br/>Produit du Mexique</div>
    <div class="up">↑↑<span>THIS SIDE UP</span></div>
  </div>
</div>
<script>
  window.onload = function () {
    try { JsBarcode("#bc", "${digits}", { format: "CODE128", width: 1.3, height: 42, displayValue: true, fontSize: 11, margin: 0 }); } catch (e) {}
    setTimeout(function () { window.print(); }, 500);
  };
<\/script>
</body></html>`);
    w.document.close();
    w.focus();
  };

  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center bg-cream font-serif text-2xl italic text-pine">Monte Yolli…</div>;
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cream px-4 text-center">
        <Logo className="h-16 w-16" />
        <p className="font-serif text-2xl text-pine">{user ? a.restricted : a.notLoggedIn}</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          {!user && (
            <button
              type="button"
              data-testid="admin-login-button"
              onClick={() => setAuthOpen(true)}
              className="bg-pine px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-copper"
            >
              {t.nav.login}
            </button>
          )}
          <a href="/" data-testid="admin-back-home" className="border border-pine/25 px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-pine transition-colors hover:border-copper hover:text-copper">
            {a.backHome}
          </a>
        </div>
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onAuth={(u) => setUser(u)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-page">
      <header className="border-b border-pine/10 bg-paper">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-3">
            <Logo className="h-10 w-10" />
            <span className="font-serif text-2xl text-pine">
              {a.title} <span className="italic text-copper">Monte Yolli</span>
            </span>
          </div>
          <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-sand transition-colors hover:text-copper">
            <ArrowLeft className="h-4 w-4" />
            {a.backHome}
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <div className="mb-8 flex flex-wrap gap-2">
          {[
            ["requests", a.tabRequests],
            ["articles", a.tabArticles],
            ["config", a.tabConfig],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === key ? "bg-pine text-cream" : "border border-pine/15 text-sand hover:text-copper"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "requests" && (
          <div className="border border-pine/10 bg-paper p-6 shadow-sm" data-testid="admin-requests-panel">
            <div className="mb-4 flex items-center justify-between gap-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {requests !== null ? `${requests.length} · ${a.tabRequests}` : a.tabRequests}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="admin-refresh-button"
                  onClick={loadRequests}
                  className="inline-flex items-center gap-2 border border-pine/20 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-pine transition-colors duration-300 hover:border-copper hover:text-copper"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {a.refresh}
                </button>
                <button
                  type="button"
                  data-testid="admin-reset-box-button"
                  onClick={resetBox}
                  className="inline-flex items-center gap-2 border border-copper/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-copper transition-colors duration-300 hover:bg-copper hover:text-paper"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {a.resetBox}
                </button>
              </div>
            </div>
            {diag && (
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.15em] text-sand" data-testid="admin-diagnostics">
                {a.diagLine
                  .replace("{db}", diag.db_name || "—")
                  .replace("{n}", diag.requests_total)
                  .replace("{last}", diag.latest_request_at ? new Date(diag.latest_request_at).toLocaleString() : a.diagNone)}
              </p>
            )}
            <p className="mb-4 text-xs font-light text-sand" data-testid="admin-live-hint">{a.liveHint}</p>
            {requestsError && <p className="text-sm text-copper" data-testid="admin-requests-error">{a.loadError}</p>}
            {requests === null && !requestsError && <p className="text-sm text-sand">…</p>}
            {requests !== null && requests.length === 0 && <p className="text-sm font-light text-sand">{a.emptyRequests}</p>}
            {requests !== null && requests.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-pine/10 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">
                      <th className="py-3 pr-4">{a.colDate}</th>
                      <th className="py-3 pr-4">{a.colBusiness}</th>
                      <th className="py-3 pr-4">{a.colContact}</th>
                      <th className="py-3 pr-4">{a.colVolume}</th>
                      <th className="py-3 pr-4">{a.colVarieties}</th>
                      <th className="py-3 pr-4">{a.colSample}</th>
                      <th className="py-3 pr-4">{a.colMessage}</th>
                      <th className="py-3">{a.labelBtn}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} className="border-b border-pine/5 font-light text-bark" data-testid={`admin-request-row-${r.id}`}>
                        <td className="whitespace-nowrap py-3 pr-4 font-mono text-xs text-sand">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="py-3 pr-4 font-medium text-pine">{r.business}</td>
                        <td className="py-3 pr-4">
                          <div>{r.name}</div>
                          <div className="text-xs text-sand">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                          {(r.address || r.city) && (
                            <div className="text-xs text-sand">{[r.address, r.postal_code, r.city, r.country].filter(Boolean).join(", ")}</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-3 pr-4 text-xs">{r.volume}</td>
                        <td className="py-3 pr-4 text-xs">{(r.varieties || []).join(", ") || "—"}</td>
                        <td className="py-3 pr-4 text-xs">{r.sample_box ? "✓" : "—"}</td>
                        <td className="max-w-xs py-3 pr-4 text-xs text-sand">{r.message || "—"}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`admin-label-${r.id}`}
                              onClick={() => printLabel(r)}
                              title={a.printLabel}
                              aria-label={a.printLabel}
                              className="inline-flex items-center gap-2 border border-copper/30 bg-cream px-3 py-2 text-[10px] font-medium uppercase tracking-[0.15em] text-copper transition-colors duration-300 hover:bg-copper hover:text-paper"
                            >
                              <Printer className="h-3.5 w-3.5" />
                              {a.labelBtn}
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-delete-request-${r.id}`}
                              onClick={() => removeRequest(r.id)}
                              title={a.deleteRequest}
                              aria-label={a.deleteRequest}
                              className="inline-flex items-center border border-pine/15 px-2 py-2 text-sand transition-colors duration-300 hover:border-copper hover:text-copper"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "articles" && (
          <div data-testid="admin-articles-panel">
            <div className="border border-pine/10 bg-paper p-6 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="admin-article-topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder={a.topicPlaceholder}
                  className={inputCls}
                />
                <select
                  data-testid="admin-article-lang"
                  value={articleLang}
                  onChange={(e) => setArticleLang(e.target.value)}
                  className={`${inputCls} sm:w-36`}
                >
                  <option value="fr">Français</option>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
                <button
                  type="button"
                  data-testid="admin-generate-button"
                  onClick={generate}
                  disabled={generating || !topic.trim()}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-copper px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-pine disabled:opacity-60"
                >
                  <Sparkles className="h-4 w-4" />
                  {generating ? a.generating : a.generate}
                </button>
              </div>
              {error && <p className="mt-3 text-xs text-copper" data-testid="admin-generate-error">{error}</p>}
            </div>

            <div className="mt-6 flex flex-col gap-4" data-testid="admin-articles-list">
              {articles.length === 0 && <p className="text-sm font-light text-sand">{a.noArticles}</p>}
              {articles.map((art) => (
                <div key={art.id} className="flex items-start justify-between gap-4 border border-pine/10 bg-paper p-5" data-testid={`admin-article-${art.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-serif text-xl text-pine">{art.title}</h3>
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-copper">{art.lang}</span>
                    </div>
                    <p className="mt-1 text-sm font-light text-sand">{art.excerpt}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-sand">
                      {new Date(art.created_at).toLocaleDateString()} — {a.published}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid={`admin-article-delete-${art.id}`}
                    onClick={() => removeArticle(art.id)}
                    aria-label={a.deleteLabel}
                    className="shrink-0 text-sand transition-colors hover:text-copper"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "config" && (
          <div className="max-w-xl border border-pine/10 bg-paper p-6 shadow-sm sm:p-8" data-testid="admin-config-panel">
            <div className="flex flex-col gap-5">
              <div>
                <label htmlFor="cfg-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {a.contactEmailLabel}
                </label>
                <input id="cfg-email" data-testid="admin-config-email" type="email" value={settings.contact_email} onChange={(e) => setSettings((s) => ({ ...s, contact_email: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cfg-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {a.contactPhoneLabel}
                </label>
                <input id="cfg-phone" data-testid="admin-config-phone" value={settings.contact_phone} onChange={(e) => setSettings((s) => ({ ...s, contact_phone: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cfg-website" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                  {a.contactWebsiteLabel}
                </label>
                <input id="cfg-website" data-testid="admin-config-website" value={settings.contact_website} onChange={(e) => setSettings((s) => ({ ...s, contact_website: e.target.value }))} className={inputCls} />
              </div>
              <div className="border border-copper/30 bg-cream p-4">
                <label htmlFor="cfg-notif" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-copper">
                  {a.notifEmailLabel}
                </label>
                <input id="cfg-notif" data-testid="admin-config-notif" type="email" value={settings.notification_email || ""} onChange={(e) => setSettings((s) => ({ ...s, notification_email: e.target.value }))} className={inputCls} />
              </div>
              <label htmlFor="cfg-maintenance" className={`flex cursor-pointer items-start gap-4 border p-4 transition-colors duration-300 ${settings.maintenance_mode ? "border-copper bg-copper/10" : "border-pine/15 bg-white"}`}>
                <input
                  id="cfg-maintenance"
                  data-testid="admin-config-maintenance"
                  type="checkbox"
                  checked={!!settings.maintenance_mode}
                  onChange={(e) => setSettings((s) => ({ ...s, maintenance_mode: e.target.checked }))}
                  className="mt-1 h-4 w-4 accent-[#B0682C]"
                />
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-pine">{a.maintenanceLabel}</span>
                  <span className="mt-1 block text-xs font-light text-sand">{a.maintenanceHelp}</span>
                  <span className="mt-2 block text-xs font-medium text-copper" data-testid="admin-maintenance-status">
                    {settings.maintenance_mode ? a.maintenanceOn : a.maintenanceOff}
                  </span>
                </span>
              </label>
              <button
                type="button"
                data-testid="admin-config-save"
                onClick={saveSettings}
                disabled={savingSettings}
                className="bg-pine px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-copper disabled:opacity-60"
              >
                {savingSettings ? "…" : a.saveLabel}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
